/**
 * NoteCard Component
 * Display card for viewing a saved note in lists.
 * Shows content, photo thumbnail (if present), menu item name,
 * business name, and timestamp with edit/delete actions.
 * Supports inline editing with Save/Cancel buttons.
 */

import Ionicons from '@expo/vector-icons/Ionicons';
import { router } from 'expo-router';
import React, { useCallback, useState } from 'react';
import { Pressable, TextInput, View } from 'react-native';

import { ImageViewer } from '@/components/display/image-viewer';
import type { ViewerPhoto } from '@/components/display/image-viewer';
import { prefetchViewerPhotos } from '@/components/display/image-viewer.utils';
import { AppImage } from '@/components/ui/app-image';
import { Text } from '@/components/ui/text';
import { formatRelativeTime } from '@/utils/format-time';
import { Note } from '@/types/note';

export interface NoteCardProps {
  /** Note data from the API */
  note: Note;
  /** Callback when note content is saved after inline editing */
  onSave: (noteId: string, content: string) => void;
  /** Callback when the delete button is pressed */
  onDelete: (note: Note) => void;
}

export function NoteCard({ note, onSave, onDelete }: NoteCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [viewerVisible, setViewerVisible] = useState(false);

  const viewerPhotos: ViewerPhoto[] = note.photo
    ? [
        {
          id: note.photo.id,
          imageUrl: note.photo.imageUrl,
          r2Key: note.photo.r2Key,
          width: note.photo.width,
          height: note.photo.height,
          altText: note.photo.altText,
          caption: {
            dishName: note.menuItemName,
            businessName: note.businessName,
            createdAt: note.createdAt,
          },
        },
      ]
    : [];

  const handleStartEditing = useCallback(() => {
    setEditContent(note.content);
    setIsEditing(true);
  }, [note.content]);

  const handleSave = useCallback(() => {
    const trimmed = editContent.trim();
    if (trimmed.length > 0) {
      onSave(note.id, trimmed);
    }
    setIsEditing(false);
  }, [editContent, note.id, onSave]);

  const handleCancel = useCallback(() => {
    setIsEditing(false);
    setEditContent('');
  }, []);

  const handleDelete = useCallback(() => {
    onDelete(note);
  }, [note, onDelete]);

  const photoKey = note.photo ? note.photo.r2Key ?? note.photo.imageUrl : null;
  const photoLabel = note.photo?.altText
    || (note.menuItemName ? `Note photo of ${note.menuItemName}` : 'Note photo');

  const isSaveDisabled = editContent.trim().length === 0;

  return (
    <View className="border border-border rounded-xl p-4 mb-3 bg-surface">
      {/* Header: business + timestamp */}
      <View className="flex-row items-center justify-between mb-2">
        <View className="flex-row items-center flex-1 mr-2">
          <Pressable
            onPress={() => router.push({ pathname: '/(root)/business/[id]', params: { id: note.businessId } })}
            className="flex-row items-center flex-1"
            hitSlop={4}
          >
            <Ionicons
              name="location-outline"
              size={14}
              className="text-muted-foreground/70"
            />
            <Text
              className="text-xs text-muted-foreground/70 ml-1"
              numberOfLines={1}
            >
              {note.businessName || 'Unknown restaurant'}
            </Text>
          </Pressable>
        </View>
        <Text className="text-xs text-muted-foreground/70">
          {formatRelativeTime(note.createdAt)}
        </Text>
      </View>

      {/* Menu item name */}
      {note.menuItemName && (
        <Pressable
          onPress={() => router.push({ pathname: '/(root)/menu-item/[id]', params: { id: note.menuItemId } })}
          className="flex-row items-center mb-2"
          hitSlop={4}
        >
          <Ionicons
            name="restaurant-outline"
            size={14}
            className="text-foreground"
          />
          <Text className="text-sm font-semibold text-foreground ml-1.5" numberOfLines={1}>
            {note.menuItemName}
          </Text>
        </Pressable>
      )}

      {/* Content + photo row */}
      <View className="flex-row">
        {/* Content */}
        <View className="flex-1 mr-3">
          {isEditing ? (
            <TextInput
              value={editContent}
              onChangeText={setEditContent}
              multiline
              autoFocus
              className="text-sm text-foreground leading-5 border border-border rounded-lg p-2 min-h-[80px]"
              textAlignVertical="top"
            />
          ) : (
            <Text
              className="text-sm text-foreground leading-5"
              numberOfLines={4}
            >
              {note.content}
            </Text>
          )}
        </View>

        {/* Photo thumbnail */}
        {photoKey && (
          <Pressable
            onPressIn={() => prefetchViewerPhotos(viewerPhotos)}
            onPress={() => setViewerVisible(true)}
          >
            <View
              className="rounded-lg overflow-hidden bg-surface-secondary"
              style={{ width: 64, height: 64 }}
            >
              <AppImage
                r2Key={photoKey}
                preset="thumbnail"
                style={{ width: 64, height: 64 }}
                accessibilityLabel={photoLabel}
              />
            </View>
          </Pressable>
        )}
      </View>

      {/* Action buttons */}
      <View className="flex-row items-center justify-end mt-3 pt-2 border-t border-border/50">
        {isEditing ? (
          <>
            <Pressable
              onPress={handleCancel}
              hitSlop={8}
              className="flex-row items-center mr-4 py-1"
              accessibilityRole="button"
              accessibilityLabel="Cancel editing"
            >
              <Ionicons
                name="close-outline"
                size={16}
                className="text-muted-foreground"
              />
              <Text className="text-xs text-muted-foreground ml-1">
                Cancel
              </Text>
            </Pressable>

            <Pressable
              onPress={handleSave}
              disabled={isSaveDisabled}
              hitSlop={8}
              className="flex-row items-center py-1"
              style={{ opacity: isSaveDisabled ? 0.4 : 1 }}
              accessibilityRole="button"
              accessibilityLabel="Save note"
            >
              <Ionicons
                name="checkmark-outline"
                size={16}
                className="text-primary"
              />
              <Text className="text-xs text-primary ml-1">
                Save
              </Text>
            </Pressable>
          </>
        ) : (
          <>
            <Pressable
              onPress={handleStartEditing}
              hitSlop={8}
              className="flex-row items-center mr-4 py-1"
              accessibilityRole="button"
              accessibilityLabel="Edit note"
            >
              <Ionicons
                name="pencil-outline"
                size={16}
                className="text-muted-foreground"
              />
              <Text className="text-xs text-muted-foreground ml-1">
                Edit
              </Text>
            </Pressable>

            <Pressable
              onPress={handleDelete}
              hitSlop={8}
              className="flex-row items-center py-1"
              accessibilityRole="button"
              accessibilityLabel="Delete note"
            >
              <Ionicons
                name="trash-outline"
                size={16}
                className="text-destructive"
              />
              <Text className="text-xs text-destructive ml-1">
                Delete
              </Text>
            </Pressable>
          </>
        )}
      </View>

      {/* Photo lightbox */}
      {viewerPhotos.length > 0 && (
        <ImageViewer
          photos={viewerPhotos}
          initialIndex={0}
          visible={viewerVisible}
          onClose={() => setViewerVisible(false)}
        />
      )}
    </View>
  );
}

export default NoteCard;
